import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Clock, Flag, ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle, Send, LayoutGrid } from "lucide-react";

function isAnswered(sel) {
  if (sel == null) return false;
  if (Array.isArray(sel)) return sel.length > 0;
  return true;
}

export default function Az900Exam({ exam, onSubmitted, onExit }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(() => ({ ...(exam.answers || {}) }));
  const [flags, setFlags] = useState(() => new Set(exam.flags || []));
  const [remaining, setRemaining] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);

  const questions = exam.questions;
  const q = questions[idx];
  const total = questions.length;

  const answeredCount = useMemo(
    () => questions.filter((qq) => isAnswered(answers[qq.qid])).length,
    [answers, questions]
  );

  const doSubmit = useCallback(async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const r = await api.post("/az900/submit", { attempt_id: exam.id });
      onSubmitted(r.data);
    } catch (e) {
      submittedRef.current = false;
      setSubmitting(false);
      toast.error(apiErr(e));
    }
  }, [exam.id, onSubmitted]);

  // Countdown timer
  useEffect(() => {
    const start = exam.started_at ? new Date(exam.started_at).getTime() : Date.now();
    const end = start + exam.minutes * 60 * 1000;
    const tick = () => {
      const rem = Math.max(0, Math.round((end - Date.now()) / 1000));
      setRemaining(rem);
      if (rem <= 0) {
        toast.warning("Time is up — submitting your exam.");
        doSubmit();
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [exam.started_at, exam.minutes, doSubmit]);

  const saveAnswer = async (qid, selected) => {
    setAnswers((p) => ({ ...p, [qid]: selected }));
    try {
      await api.post("/az900/answer", { attempt_id: exam.id, qid, selected });
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const selectSingle = (i) => saveAnswer(q.qid, i);

  const toggleMulti = (i) => {
    const cur = Array.isArray(answers[q.qid]) ? answers[q.qid] : [];
    const next = cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i].sort((a, b) => a - b);
    saveAnswer(q.qid, next);
  };

  const toggleFlag = async () => {
    const flagged = !flags.has(q.qid);
    setFlags((p) => {
      const n = new Set(p);
      flagged ? n.add(q.qid) : n.delete(q.qid);
      return n;
    });
    try {
      await api.post("/az900/flag", { attempt_id: exam.id, qid: q.qid, flagged });
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const isSelected = (i) => {
    const sel = answers[q.qid];
    if (q.type === "multi") return Array.isArray(sel) && sel.includes(i);
    return sel === i;
  };

  const mins = remaining == null ? "--" : String(Math.floor(remaining / 60)).padStart(2, "0");
  const secs = remaining == null ? "--" : String(remaining % 60).padStart(2, "0");
  const lowTime = remaining != null && remaining <= 300;

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="az900-exam">
      {/* Top bar */}
      <div className="sticky top-0 z-20 border-b border-border bg-card/95 backdrop-blur px-6 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-md bg-[#0078d4] grid place-items-center text-white text-xs font-bold">AZ</div>
          <div>
            <div className="font-display font-bold text-sm leading-none">AZ-900 · {exam.mode === "full" ? "Full Simulation" : exam.mode === "practice" ? "Practice Exam" : "Quick Quiz"}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{answeredCount} of {total} answered</div>
          </div>
        </div>
        <div
          data-testid="az900-timer"
          className={`flex items-center gap-2 font-mono text-lg font-bold px-3 py-1.5 rounded-lg border ${
            lowTime ? "text-rose-600 border-rose-500/40 bg-rose-500/10 animate-pulse" : "text-[#0078d4] border-[#0078d4]/30 bg-[#0078d4]/5"
          }`}
        >
          <Clock className="h-4 w-4" /> {mins}:{secs}
        </div>
        <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)} data-testid="az900-submit-open" className="gap-2">
          <Send className="h-4 w-4" /> Submit
        </Button>
      </div>

      <div className="flex-1 flex gap-6 p-6 max-w-7xl mx-auto w-full">
        {/* Question area */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold px-2 py-1 rounded bg-[#0078d4]/10 text-[#0078d4]">{q.domain_name}</span>
              <span className="text-xs text-muted-foreground capitalize">{q.objective} · {q.difficulty}</span>
            </div>
            <Button
              variant={flags.has(q.qid) ? "default" : "outline"}
              size="sm"
              onClick={toggleFlag}
              data-testid="az900-flag-btn"
              className={`gap-2 ${flags.has(q.qid) ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}`}
            >
              <Flag className="h-4 w-4" /> {flags.has(q.qid) ? "Flagged" : "Flag"}
            </Button>
          </div>

          <div className="mt-4 rounded-xl border border-border bg-card p-6" data-testid="az900-question">
            <div className="text-xs text-muted-foreground font-medium">Question {idx + 1} of {total}</div>
            <h2 className="text-lg font-semibold mt-2 leading-relaxed">{q.prompt}</h2>
            {q.type === "multi" && (
              <div className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400">Select all that apply.</div>
            )}

            <div className="mt-5 space-y-3">
              {q.options.map((opt, i) => (
                <button
                  key={i}
                  data-testid={`az900-option-${i}`}
                  onClick={() => (q.type === "multi" ? toggleMulti(i) : selectSingle(i))}
                  className={`w-full text-left flex items-center gap-3 rounded-lg border px-4 py-3.5 transition-colors ${
                    isSelected(i)
                      ? "border-[#0078d4] bg-[#0078d4]/10"
                      : "border-border hover:border-[#0078d4]/40 hover:bg-accent"
                  }`}
                >
                  <span
                    className={`shrink-0 h-5 w-5 grid place-items-center border-2 ${q.type === "multi" ? "rounded" : "rounded-full"} ${
                      isSelected(i) ? "border-[#0078d4] bg-[#0078d4] text-white" : "border-muted-foreground/40"
                    }`}
                  >
                    {isSelected(i) && <CheckCircle2 className="h-3.5 w-3.5" />}
                  </span>
                  <span className="text-sm">{opt}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between">
            <Button variant="outline" disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))} data-testid="az900-prev" className="gap-2">
              <ChevronLeft className="h-4 w-4" /> Previous
            </Button>
            {idx < total - 1 ? (
              <Button onClick={() => setIdx((i) => Math.min(total - 1, i + 1))} data-testid="az900-next" className="gap-2 bg-[#0078d4] hover:bg-[#106ebe]">
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={() => setConfirmOpen(true)} data-testid="az900-finish" className="gap-2 bg-emerald-600 hover:bg-emerald-700">
                Review & Submit <Send className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Palette */}
        <div className="w-64 shrink-0 hidden lg:block">
          <div className="sticky top-24 rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <LayoutGrid className="h-4 w-4 text-[#0078d4]" /> Questions
            </div>
            <div className="grid grid-cols-6 gap-2 mt-3" data-testid="az900-palette">
              {questions.map((qq, i) => {
                const ans = isAnswered(answers[qq.qid]);
                const fl = flags.has(qq.qid);
                const cur = i === idx;
                return (
                  <button
                    key={qq.qid}
                    data-testid={`az900-palette-${i}`}
                    onClick={() => setIdx(i)}
                    className={`relative h-8 w-8 rounded-md text-xs font-mono font-semibold grid place-items-center border transition-colors ${
                      cur ? "ring-2 ring-[#0078d4] ring-offset-1 ring-offset-card" : ""
                    } ${ans ? "bg-[#0078d4] text-white border-[#0078d4]" : "bg-muted text-muted-foreground border-border hover:border-[#0078d4]/50"}`}
                  >
                    {i + 1}
                    {fl && <Flag className="absolute -top-1 -right-1 h-3 w-3 text-amber-500 fill-amber-500" />}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 space-y-1.5 text-[11px] text-muted-foreground">
              <div className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-[#0078d4]" /> Answered</div>
              <div className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-muted border border-border" /> Unanswered</div>
              <div className="flex items-center gap-2"><Flag className="h-3 w-3 text-amber-500 fill-amber-500" /> Flagged for review</div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="az900-submit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" /> Submit your exam?
            </DialogTitle>
            <DialogDescription>
              You've answered <span className="font-semibold text-foreground">{answeredCount}</span> of {total} questions
              {answeredCount < total && ` (${total - answeredCount} unanswered)`}.
              {flags.size > 0 && ` ${flags.size} flagged for review.`} Once submitted, you can't change your answers.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="az900-submit-cancel">Keep working</Button>
            <Button onClick={doSubmit} disabled={submitting} data-testid="az900-submit-confirm" className="bg-emerald-600 hover:bg-emerald-700 gap-2">
              <Send className="h-4 w-4" /> {submitting ? "Submitting…" : "Submit exam"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
